import { Box, FormGroup } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import ForwardOption from './ForwardOption';
import { WorkflowOption } from '../../../../../redux/project/sliceProjectWorkflow';
import { groupBy } from 'lodash';
import { ForwardState, StateChangedArguments } from '../MainDialog/MainWorkflowDialog';
import { useTranslation } from 'react-i18next';
import { mdiAlertCircle } from '@mdi/js';
import { vars } from '../../../../../Theme';
import Message from '../../_parts/Message';

export const ForwardOptionsWrapper = ({
    data,
    parentValue,
    disabled,
    getStateChanged,
    selected,
    handleChangeProp,
    hasError,
}: {
    parentValue: string;
    data: WorkflowOption[];
    selected: {
        [key: string]: ForwardState;
    };
    getStateChanged: (arg: StateChangedArguments) => void;
    handleChangeProp?: (
        event: React.ChangeEvent<HTMLInputElement>,
        type: 'check' | 'radio',
    ) => void;
    disabled?: boolean;
    hasError?: boolean;
}) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'mainDialog' });
    const grouped = useMemo(() => groupBy(data, 'connectorType'), [data]);

    const multiSelectComputed = useMemo(
        () => selected?.[parentValue]?.multi ?? [],
        [selected?.[parentValue]],
    );

    const exclusiveSelectComputed = useMemo(
        () => selected?.[parentValue]?.exclusive ?? [],
        [selected?.[parentValue]],
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'check' | 'radio') => {
        const { value } = event.target;

        if (type === 'radio') {
            getStateChanged({
                parentValue,
                state: { exclusive: [value], multi: multiSelectComputed },
            });
        }
        if (type === 'check') {
            getStateChanged({
                parentValue,
                state: {
                    exclusive: exclusiveSelectComputed,
                    multi: multiSelectComputed.includes(value)
                        ? multiSelectComputed.filter(item => item !== value)
                        : [...multiSelectComputed, value],
                },
            });
        }
    };

    useEffect(() => {
        if (disabled) {
            getStateChanged({
                parentValue,
                state: {
                    exclusive: [],
                    multi: [],
                },
            });
        }
    }, [disabled]);

    const showErrorCondition = () => {
        return hasError && !exclusiveSelectComputed.length && !multiSelectComputed.length;
    };

    return (
        <Box>
            {showErrorCondition() && (
                <Message
                    text={t('errorMakeASelection')}
                    icon={mdiAlertCircle}
                    color={vars.errorDialogRed}
                    typographySx={{ fontWeight: 700 }}
                    wrapperSx={{
                        border: 'none',
                        padding: '2px',
                        mb: 1,
                    }}
                />
            )}

            {grouped['ALWAYS'] && (
                <FormGroup>
                    {grouped['ALWAYS'].map(option => {
                        return (
                            <ForwardOption
                                key={`${option.optionId}`}
                                option={option}
                                checked={true}
                                type='radio'
                            />
                        );
                    })}
                </FormGroup>
            )}
            {grouped['EXCLUSIVE_CHOICE'] && (
                <FormGroup>
                    {grouped['EXCLUSIVE_CHOICE'].map(option => {
                        if (!option.connectorSubSelection.length) {
                            return (
                                <ForwardOption
                                    key={`${option.optionId}`}
                                    option={option}
                                    checked={exclusiveSelectComputed.includes(option.path)}
                                    handleChange={ev => handleChange(ev, 'radio')}
                                    type='radio'
                                    disabled={disabled}
                                    hasError={showErrorCondition()}
                                />
                            );
                        } else {
                            return (
                                <ForwardOption
                                    key={`${option.optionId}`}
                                    option={option}
                                    checked={exclusiveSelectComputed.includes(option.path)}
                                    handleChange={ev => handleChange(ev, 'radio')}
                                    type='radio'
                                    hasError={showErrorCondition()}
                                >
                                    {exclusiveSelectComputed.includes(option.path) && (
                                        <ForwardOptionsWrapper
                                            parentValue={option.path}
                                            getStateChanged={getStateChanged}
                                            selected={selected}
                                            handleChangeProp={
                                                handleChangeProp ??
                                                (ev => {
                                                    handleChange(
                                                        ev,
                                                        option.connectorSubSelection[0]
                                                            .connectorType === 'EXCLUSIVE_CHOICE'
                                                            ? 'radio'
                                                            : 'check',
                                                    );
                                                })
                                            }
                                            key={`${option.optionId}`}
                                            data={option.connectorSubSelection}
                                            disabled={
                                                !exclusiveSelectComputed.includes(option.path)
                                            }
                                            hasError={hasError}
                                        ></ForwardOptionsWrapper>
                                    )}
                                </ForwardOption>
                            );
                        }
                    })}
                </FormGroup>
            )}
            {grouped['MULTIPLE_CHOICE'] && (
                <FormGroup>
                    {grouped['MULTIPLE_CHOICE'].map(option => {
                        if (!option.connectorSubSelection.length) {
                            return (
                                <ForwardOption
                                    key={`${option.optionId}`}
                                    option={option}
                                    type='check'
                                    checked={multiSelectComputed.includes(option.path)}
                                    disabled={disabled}
                                    handleChange={ev => handleChange(ev, 'check')}
                                    hasError={showErrorCondition()}
                                />
                            );
                        } else {
                            return (
                                <ForwardOption
                                    key={`${option.optionId}`}
                                    option={option}
                                    type='check'
                                    checked={multiSelectComputed.includes(option.path)}
                                    handleChange={ev => handleChange(ev, 'check')}
                                    hasError={showErrorCondition()}
                                >
                                    {multiSelectComputed.includes(option.path) && (
                                        <ForwardOptionsWrapper
                                            getStateChanged={getStateChanged}
                                            parentValue={option.path}
                                            selected={selected}
                                            handleChangeProp={
                                                handleChangeProp ??
                                                (ev =>
                                                    handleChange(
                                                        ev,
                                                        option.connectorSubSelection[0]
                                                            .connectorType === 'EXCLUSIVE_CHOICE'
                                                            ? 'radio'
                                                            : 'check',
                                                    ))
                                            }
                                            key={`${option.optionId}`}
                                            data={option.connectorSubSelection}
                                            disabled={!multiSelectComputed.includes(option.path)}
                                            hasError={hasError}
                                        ></ForwardOptionsWrapper>
                                    )}
                                </ForwardOption>
                            );
                        }
                    })}
                </FormGroup>
            )}
        </Box>
    );
};
